import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Sparkle, Check, Hourglass } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, "0");

const PromoCountdown = ({ until }) => {
    const [left, setLeft] = useState(0);
    useEffect(() => {
        const tick = () => setLeft(Math.max(0, new Date(until).getTime() - Date.now()));
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [until]);
    const h = Math.floor(left / 3600000);
    const m = Math.floor(left / 60000) % 60;
    const s = Math.floor(left / 1000) % 60;
    return (
        <div
            data-testid="promo-countdown"
            className="mt-8 inline-flex items-center gap-3 rounded-full border border-[#ffd700]/30 bg-[#ffd700]/5 px-6 py-3"
        >
            <Hourglass className="h-4 w-4 animate-pulse-soft text-[#ffd700]/80" />
            <span className="font-cinzel text-xs tracking-[0.25em] text-[#ffd700]/90">THE FREE HOURS CLOSE IN</span>
            <span data-testid="promo-countdown-timer" className="font-mono text-lg tracking-widest text-[#f8f7ff]">
                {pad(h)}:{pad(m)}:{pad(s)}
            </span>
        </div>
    );
};

export const READINGS = [
    {
        id: "reading_30",
        testid: "reading-card-30",
        title: "The Half-Hour Crossing",
        price: "$120",
        minutes: 30,
        kind: "time",
        description: "Thirty live minutes with Mara inside the Back Room — the full shape of what was taken, and where it went.",
        features: ["30 minutes live with Mara", "Trace the diverted blessing", "Ask what the mirror shows"],
    },
    {
        id: "reading_45",
        testid: "reading-card-45",
        title: "The Three-Quarter Crossing",
        price: "$180",
        minutes: 45,
        kind: "time",
        description: "Forty-five minutes to follow both paths — yours and hers — to the moment they reversed.",
        features: ["45 minutes live with Mara", "Map both life-threads", "First steps of the reclamation"],
    },
    {
        id: "reading_60",
        testid: "reading-card-60",
        title: "The Full Hour Reversal",
        price: "$240",
        minutes: 60,
        kind: "time",
        description: "A full hour — the complete picture of what was taken, and everything that can still be called back.",
        features: ["60 minutes live with Mara", "Full reversal reading", "Personal guidance from Mara"],
    },
    {
        id: "ritual_fate_reclamation",
        testid: "reading-card-ritual-fate",
        title: "Fate Reclamation Ritual + Reading",
        price: "$39",
        kind: "ritual",
        description: "Mara performs the reclamation ritual for you, then reads what returned across the mirror.",
        features: ["Ritual performed by Mara herself", "Full reading of what was recovered", "Written reclamation report"],
    },
    {
        id: "ritual_return_my_beauty",
        testid: "reading-card-ritual-beauty",
        title: "Return My Beauty Spell",
        price: "$39",
        kind: "ritual",
        description: "For those who watched their glow gather on another woman's face — a working to call your beauty back from the life that borrowed it.",
        features: ["Beauty-reversal working by Mara", "Mirror-side confirmation reading", "Written note of what returned"],
    },
];

export const Readings = ({ onSelect }) => {
    const [promo, setPromo] = useState(null);
    useEffect(() => {
        axios.get(`${API}/promo`).then((r) => setPromo(r.data)).catch(() => {});
    }, []);
    const free = !!promo?.active;
    return (
        <section id="readings" data-testid="readings-section" className="relative px-5 py-28 sm:px-8 lg:py-36">
            <div className="nebula-blob left-[-5%] bottom-[10%] h-[28rem] w-[28rem] bg-[#2e0b53]/40" />
            <div className="mx-auto max-w-7xl">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="mb-16 max-w-2xl"
                >
                    <p className="eyebrow mb-4">Chapter VII · The Sister Readings</p>
                    <h2 className="text-glow-lilac font-display text-2xl font-bold tracking-wide text-[#f8f7ff] sm:text-3xl lg:text-4xl">
                        BOOK YOUR CROSSING
                    </h2>
                    <p className="mt-6 font-cormorant text-lg italic leading-relaxed text-[#e0aaff]/80">
                        Readings begin at $120 for a half hour — $4 per minute. You pay nothing now: Mara weighs every
                        request herself, and only after she accepts do you receive the payment mirror.
                        {free && " While the mirror stands open, five live minutes are also free inside the Back Room."}
                    </p>
                    {free && promo?.until && <PromoCountdown until={promo.until} />}
                </motion.div>

                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {READINGS.map((r, i) => (
                        <motion.article
                            key={r.id}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.8, delay: i * 0.1 }}
                            data-testid={r.testid}
                            className="glass-card glass-card-hover relative flex flex-col overflow-hidden p-6"
                        >
                            <div className="mirror-sheen pointer-events-none absolute inset-0 opacity-30" />
                            <Sparkle className="mb-5 h-5 w-5 text-[#c77dff]" />
                            <h3 className="font-cinzel text-base font-semibold leading-snug tracking-wide text-[#f8f7ff]">
                                {r.title}
                            </h3>
                            <p className="mt-4 font-display text-3xl font-bold text-[#e0aaff] text-glow-lilac">{r.price}</p>
                            {r.kind === "time" && (
                                <p className="mt-1 font-cinzel text-[9px] tracking-[0.3em] text-[#c77dff]/60">
                                    $4 PER MINUTE · PAY ONLY IF MARA ACCEPTS
                                </p>
                            )}
                            <p className="mt-4 font-cormorant text-base italic leading-relaxed text-[#e0aaff]/75">
                                {r.description}
                            </p>
                            <ul className="mt-6 flex-1 space-y-3">
                                {r.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#f8f7ff]/80">
                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#c77dff]" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                data-testid={`reading-book-button-${r.id}`}
                                onClick={() => onSelect(r.id)}
                                className="btn-ghost-glow mt-8 w-full"
                            >
                                REQUEST THIS READING
                            </button>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
};
